import React, { useRef, useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CTASectionProps {
  className?: string;
  onContactSalesClick?: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({ className = '', onContactSalesClick }) => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`py-24 lg:py-32 bg-slate-900 relative overflow-hidden ${className}`}
    >
      {/* Subtle background accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-[0.06] pointer-events-none">
        <div className="w-full h-full rounded-full bg-[#1bb4d7] blur-3xl" />
      </div>
      <div className="absolute bottom-0 left-0 w-80 h-80 opacity-[0.04] pointer-events-none">
        <div className="w-full h-full rounded-full bg-[#1bb4d7] blur-3xl" />
      </div>

      {/* Decorative grid lines */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div
          className={`max-w-4xl mx-auto text-center space-y-8 transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/8 rounded-full px-6 py-3 border border-white/15">
            <Icon name="RocketLaunchIcon" size={18} variant="solid" className="text-[#1bb4d7]" />
            <span className="font-body text-sm font-semibold text-white/80 tracking-wide">Start Your Transformation Today</span>
          </div>

          {/* Heading */}
          <h2 className="font-display font-bold text-3xl lg:text-5xl xl:text-6xl text-white leading-tight tracking-tight">
            Ready to Transform Your<br className="hidden lg:block" /> Customer Engagement?
          </h2>

          {/* Description */}
          <p className="font-body text-lg lg:text-xl text-white/60 leading-relaxed max-w-2xl mx-auto">
            Join 100+ enterprise leaders who have already transformed their customer experience with UnifyMax's AI-powered platform.
          </p>

          {/* CTA Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <button
              onClick={onContactSalesClick}
              className="inline-flex items-center justify-center space-x-2 px-10 py-5 bg-[#1bb4d7] text-white rounded-none font-cta text-lg font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 group"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={22} variant="solid" />
              <span>Contact Sales</span>
              <Icon name="ArrowRightIcon" size={18} variant="solid" className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>

          {/* Trust row */}
          <div className="flex flex-wrap items-center justify-center gap-8 pt-8 border-t border-white/10">
            {[
              { icon: 'ShieldCheckIcon', label: 'Enterprise Security' },
              { icon: 'ClockIcon', label: '24/7 Support' },
              { icon: 'CheckBadgeIcon', label: 'Proven ROI' },
            ].map((item) => (
              <div key={item.label} className="flex items-center space-x-2">
                <Icon name={item.icon as any} size={16} variant="solid" className="text-[#1bb4d7]" />
                <span className="font-body text-sm text-white/50">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
